import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

interface MetaTagsProps {
  title?: string
  description?: string
  image?: string
  type?: 'website' | 'article' | 'product'
  author?: string
  publishedTime?: string
  keywords?: string
  noindex?: boolean
}

const MetaTags = ({
  title = 'FotoLivro Store — Suas Memórias Merecem ser Tocadas',
  description = 'Suas Memórias Merecem ser Tocadas',
  image = '/images/banner-13mil-historias.png',
  type = 'website',
  author,
  publishedTime,
  keywords = 'fotolivro, álbum de fotos, fotolivro personalizado, casamento, formatura, viagem, fotografia, álbum fotográfico, presente personalizado',
  noindex = false,
}: MetaTagsProps) => {
  const location = useLocation()
  const baseUrl =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://fotolivro.store'
  const currentUrl = `${baseUrl}${location.pathname}`
  const fullImageUrl = image.startsWith('http') ? image : `${baseUrl}${image}`

  // Use exact values without truncation
  const optimizedTitle = title

  // Use exact values without truncation
  const optimizedDescription = description

  useEffect(() => {
    // Update page title
    document.title = optimizedTitle

    // Helper function to update or create meta tag
    const updateMetaTag = (
      property: string,
      content: string,
      isName: boolean = false,
    ) => {
      const attribute = isName ? 'name' : 'property'
      let element = document.querySelector(`meta[${attribute}="${property}"]`)

      if (!element) {
        element = document.createElement('meta')
        element.setAttribute(attribute, property)
        document.head.appendChild(element)
      }

      element.setAttribute('content', content)
    }

    // Helper to update or create link tag
    const updateLinkTag = (rel: string, href: string) => {
      let element = document.querySelector(
        `link[rel="${rel}"]`,
      ) as HTMLLinkElement

      if (!element) {
        element = document.createElement('link')
        element.rel = rel
        document.head.appendChild(element)
      }

      element.href = href
    }

    // Basic meta tags
    updateMetaTag('description', optimizedDescription, true)
    updateMetaTag('keywords', keywords, true)
    updateMetaTag('author', author || 'FotoLivro Store', true)

    // Robots meta
    updateMetaTag(
      'robots',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      true,
    )
    updateMetaTag(
      'googlebot',
      noindex
        ? 'noindex, nofollow'
        : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
      true,
    )

    // Open Graph (Facebook, LinkedIn, etc)
    updateMetaTag('og:title', optimizedTitle)
    updateMetaTag('og:description', optimizedDescription)
    updateMetaTag('og:image', fullImageUrl)
    updateMetaTag('og:image:width', '1200')
    updateMetaTag('og:image:height', '630')
    updateMetaTag('og:image:alt', optimizedTitle)
    updateMetaTag('og:url', currentUrl)
    updateMetaTag('og:type', type)
    updateMetaTag('og:site_name', 'FotoLivro Store')
    updateMetaTag('og:locale', 'pt_BR')

    // Twitter Cards
    updateMetaTag('twitter:card', 'summary_large_image', true)
    updateMetaTag('twitter:title', optimizedTitle, true)
    updateMetaTag('twitter:description', optimizedDescription, true)
    updateMetaTag('twitter:image', fullImageUrl, true)
    updateMetaTag('twitter:image:alt', optimizedTitle, true)
    updateMetaTag('twitter:site', '@fotolivrostore', true)
    updateMetaTag('twitter:creator', '@fotolivrostore', true)

    // Article-specific tags
    if (type === 'article' && publishedTime) {
      updateMetaTag('article:published_time', publishedTime)
      updateMetaTag('article:author', author || 'FotoLivro Store')
      updateMetaTag('article:section', 'Fotolivros')
    }

    // Product-specific tags
    if (type === 'product') {
      updateMetaTag('product:price:currency', 'BRL')
      updateMetaTag('product:availability', 'in stock')
      updateMetaTag('product:brand', 'FotoLivro Store')
    }

    // Canonical URL
    updateLinkTag('canonical', currentUrl)

    // Additional SEO meta tags
    updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', true)
    updateMetaTag('theme-color', '#2a7a7a', true)
    updateMetaTag('format-detection', 'telephone=no', true)
    updateMetaTag('mobile-web-app-capable', 'yes', true)
    updateMetaTag('apple-mobile-web-app-capable', 'yes', true)
    updateMetaTag('apple-mobile-web-app-status-bar-style', 'default', true)

    // Geo tags for local SEO
    updateMetaTag('geo.region', 'BR-PR', true)
    updateMetaTag('geo.placename', 'Curitiba', true)
    updateMetaTag('geo.position', '-25.4480;-49.2357', true)
    updateMetaTag('ICBM', '-25.4480, -49.2357', true)
  }, [
    optimizedTitle,
    optimizedDescription,
    fullImageUrl,
    type,
    currentUrl,
    author,
    publishedTime,
    keywords,
    noindex,
  ])

  return null
}

export default MetaTags
